import { useEffect, useState, useCallback } from "react";
import api, { apiError, SERVICE_OPTIONS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Upload, Pencil, Trash2, Search } from "lucide-react";

const EMPTY = { first_name: "", last_name: "", student_id: "", grade: "", teacher: "", school_id: "", client_id: "", services: [], provider_ids: [] };

const STATUS_BADGE = {
  available: <Badge className="gap-1.5 rounded-full border-0 bg-slate-100 text-slate-600 hover:bg-slate-100"><span className="h-1.5 w-1.5 rounded-full bg-slate-400" />Available</Badge>,
  in_service: <Badge className="gap-1.5 rounded-full border-0 bg-emerald-50 text-emerald-700 hover:bg-emerald-50"><span className="pulse-dot h-1.5 w-1.5 rounded-full bg-emerald-500" />In Service</Badge>,
};

export default function StudentsPage() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "client_admin"].includes(user?.role);
  const [students, setStudents] = useState([]);
  const [schools, setSchools] = useState([]);
  const [providers, setProviders] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [teachers, setTeachers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importClient, setImportClient] = useState("");
  const [importResult, setImportResult] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (classFilter !== "all") params.teacher = classFilter;
    api.get("/students", { params }).then((r) => setStudents(r.data));
  }, [search, classFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/schools").then((r) => setSchools(r.data));
    if (canEdit) {
      api.get("/users", { params: { role: "provider" } }).then((r) => setProviders(r.data));
      api.get("/teachers").then((r) => setTeachers(r.data));
    }
    if (user?.role === "super_admin") api.get("/clients").then((r) => setClients(r.data));
  }, [user, canEdit]);

  const openAdd = () => { setForm({ ...EMPTY, client_id: user.client_id || "" }); setEditId(null); setDialogOpen(true); };
  const openEdit = (s) => {
    setForm({ first_name: s.first_name, last_name: s.last_name, student_id: s.student_id, grade: s.grade,
      teacher: s.teacher || "", school_id: s.school_id, client_id: s.client_id, services: s.services, provider_ids: s.provider_ids });
    setEditId(s.id); setDialogOpen(true);
  };

  const save = async () => {
    try {
      if (editId) await api.put(`/students/${editId}`, form);
      else await api.post("/students", form);
      toast.success(editId ? "Student updated" : "Student added");
      setDialogOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.first_name} ${s.last_name}?`)) return;
    try { await api.delete(`/students/${s.id}`); toast.success("Student deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doImport = async () => {
    if (!importFile) return toast.error("Choose a CSV file first");
    const fd = new FormData();
    fd.append("file", importFile);
    try {
      const params = user.role === "super_admin" ? { client_id: importClient } : {};
      const { data } = await api.post("/students/import", fd, { params });
      setImportResult(data);
      toast.success(`Imported ${data.created} students`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleIn = (list, val) => (list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);
  const formSchools = user?.role === "super_admin" ? schools.filter((s) => s.client_id === form.client_id) : schools;
  const formProviders = user?.role === "super_admin" ? providers.filter((p) => p.client_id === form.client_id) : providers;
  const clientName = (id) => clients.find((c) => c.id === id)?.name || "";

  return (
    <div className="space-y-6 fade-up" data-testid="students-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kicker mb-1.5">Roster</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
            {user?.role === "provider" ? "My Caseload" : "Students"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{students.length} student{students.length !== 1 && "s"}</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setImportOpen(true); setImportResult(null); }} data-testid="import-csv-button" className="rounded-full">
              <Upload size={15} className="mr-1.5" /> Import CSV
            </Button>
            <Button onClick={openAdd} data-testid="add-student-button" className="rounded-full bg-primary hover:bg-primary/90">
              <Plus size={15} className="mr-1.5" /> Add Student
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or ID..."
            className="bg-white pl-9" data-testid="student-search-input" />
        </div>
        {teachers.length > 0 && (
          <Select value={classFilter} onValueChange={setClassFilter}>
            <SelectTrigger className="w-48 bg-white" data-testid="students-class-filter"><SelectValue placeholder="All classes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All classes</SelectItem>
              {teachers.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft">
        <Table data-testid="students-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Student ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Grade</TableHead>
              <TableHead>Teacher</TableHead>
              <TableHead>School</TableHead>
              <TableHead>Services</TableHead>
              <TableHead>Providers</TableHead>
              <TableHead>Status</TableHead>
              {canEdit && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {students.map((s) => (
              <TableRow key={s.id} data-testid={`student-row-${s.student_id}`}>
                <TableCell className="font-mono text-sm">{s.student_id}</TableCell>
                <TableCell className="font-medium">{s.first_name} {s.last_name}</TableCell>
                <TableCell>{s.grade}</TableCell>
                <TableCell className="text-slate-600">{s.teacher || "—"}</TableCell>
                <TableCell className="text-slate-500">{s.school_name}</TableCell>
                <TableCell className="text-sm text-slate-600">{s.services.join(", ") || "—"}</TableCell>
                <TableCell className="text-sm text-slate-600">{s.provider_names.join(", ") || "—"}</TableCell>
                <TableCell>{STATUS_BADGE[s.status] || s.status}</TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <button onClick={() => openEdit(s)} className="mr-2 text-slate-400 transition-colors duration-200 hover:text-slate-900" data-testid={`edit-student-${s.student_id}`}><Pencil size={15} /></button>
                    <button onClick={() => remove(s)} className="text-slate-400 transition-colors duration-200 hover:text-red-600" data-testid={`delete-student-${s.student_id}`}><Trash2 size={15} /></button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {students.length === 0 && (
              <TableRow><TableCell colSpan={9} className="py-8 text-center text-slate-400">No students found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit Student" : "Add Student"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {user?.role === "super_admin" && (
              <div className="space-y-1.5">
                <Label>Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v, school_id: "", provider_ids: [] })}>
                  <SelectTrigger data-testid="student-client-select"><SelectValue placeholder="Select client">{clientName(form.client_id)}</SelectValue></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>First name</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="student-first-name-input" /></div>
              <div className="space-y-1.5"><Label>Last name</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="student-last-name-input" /></div>
              <div className="space-y-1.5"><Label>Student ID</Label>
                <Input value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} data-testid="student-id-input" /></div>
              <div className="space-y-1.5"><Label>Grade</Label>
                <Input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} data-testid="student-grade-input" /></div>
              <div className="col-span-2 space-y-1.5"><Label>Teacher / Classroom</Label>
                <Input value={form.teacher} onChange={(e) => setForm({ ...form, teacher: e.target.value })} placeholder="e.g. McNeal" data-testid="student-teacher-input" /></div>
            </div>
            <div className="space-y-1.5">
              <Label>School</Label>
              <Select value={form.school_id} onValueChange={(v) => setForm({ ...form, school_id: v })}>
                <SelectTrigger data-testid="student-school-select"><SelectValue placeholder="Select school">{formSchools.find((s) => s.id === form.school_id)?.name}</SelectValue></SelectTrigger>
                <SelectContent>{formSchools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Services</Label>
              <div className="grid grid-cols-2 gap-2">
                {SERVICE_OPTIONS.map((svc) => (
                  <label key={svc} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.services.includes(svc)} onCheckedChange={() => setForm({ ...form, services: toggleIn(form.services, svc) })}
                      data-testid={`service-checkbox-${svc.toLowerCase().replace(/ /g, "-")}`} />
                    {svc}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Assigned providers</Label>
              <div className="space-y-2">
                {formProviders.length === 0 && <div className="text-sm text-slate-400">No providers available</div>}
                {formProviders.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.provider_ids.includes(p.id)} onCheckedChange={() => setForm({ ...form, provider_ids: toggleIn(form.provider_ids, p.id) })}
                      data-testid={`provider-checkbox-${p.email}`} />
                    {p.name} <span className="text-slate-400">({p.email})</span>
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={save} className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="save-student-button">
              {editId ? "Save changes" : "Add student"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Bulk Import via CSV</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md bg-slate-50 p-3 font-mono text-xs text-slate-600">
              first_name,last_name,student_id,grade,teacher,school,services,provider_emails<br />
              John,Doe,KA-2001,5,Kingsman Main Campus,Speech Therapy;Counseling,provider@tagverify.demo
            </div>
            {user?.role === "super_admin" && (
              <div className="space-y-1.5">
                <Label>Client</Label>
                <Select value={importClient} onValueChange={setImportClient}>
                  <SelectTrigger data-testid="import-client-select"><SelectValue placeholder="Select client">{clientName(importClient)}</SelectValue></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <Input type="file" accept=".csv" onChange={(e) => setImportFile(e.target.files[0])} data-testid="import-file-input" />
            {importResult && (
              <div className="rounded-md border border-slate-200 p-3 text-sm" data-testid="import-result">
                <div className="font-semibold text-green-700">{importResult.created} students created</div>
                {importResult.errors.map((err, i) => <div key={i} className="text-red-600">{err}</div>)}
              </div>
            )}
            <Button onClick={doImport} className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="import-submit-button">Import</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
